import { isEmpty, keys } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import ContactComponent from "../../../components/ContactComponent";
import FeatureExplanation from "../../../components/FeatureExplanation";
import FilterLabels from "../../../components/labels/FilterLabels";
import VirtualScroll from "../../../components/VirtualScroll";
import { useAuthentication } from "../../../context/authentication";
import { useContacts } from "../../../context/contacts";
import { useFilteredList } from "../../../context/filteredList";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useMembers } from "../../../context/members/MembersProvider";
import { useViewport } from "../../../context/viewport";
import { useWorkspaces } from "../../../context/workspaces/WorkspacesProvider";
import classNames from "../../../utils/classNames";
import { breakpoints, Entity } from "../../../utils/constants";
import { Contact } from "../../../utils/types";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import { contactsExplanationParagraphs } from "../../Workspace/ContactsData";
import ContactActionsButton from "../../Workspace/ContactsData/ContactActionsButton";
import ContactFormSidebar from "../../Workspace/ContactsData/ContactFormSidebar";
import LockedWorkspace from "../../Workspace/LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../../Workspace/LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../../Workspace/LockedWorkspaceByMembers";
import useSideMenu from "./useSideMenu";

interface Props {}

const ContactsList: React.FC<Props> = () => {
  const { wrapActionWithAuth } = useAuthentication();
  const location = useLocation();
  const { currentWorkspaceAllowedForCommunityPlan } = useWorkspaces();
  const history = useHistory();
  const toggleSideMenu = useSideMenu((state) => state.toggleSideMenu);
  const { getIsReady } = useLoading();
  const { contacts } = useContacts();
  const { filteredList, smartLabelFilters } = useFilteredList();
  const { labels } = useLabels();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);
  const [currentlyEditedContact, setCurrentlyEditedContact] = useState<
    Contact | undefined
  >();
  const { currentWorkspaceIsLocked } = useWorkspaces();
  const { membersLimitExceeded } = useMembers();

  const filteredContactLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapContacts],
        labels,
        smartLabelFilters[Entity.contact],
        Entity.contact
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredContacts = useMemo(
    () => filteredList[Entity.contact],
    [filteredList]
  );

  const detailsPageMatch = matchPath<{
    id?: string;
    section?: string;
    subsection?: string;
  }>(location.pathname, {
    path: ["/contacts/:id/:section/:subsection", "/contacts/:id/:section"]
  });

  const toggleContact = useCallback(
    (contact: Contact) => {
      if (isMobile) toggleSideMenu();

      const {
        id,
        section,
        subsection
      }: {
        id?: string;
        section?: string;
        subsection?: string;
      } = detailsPageMatch?.params || {};

      if (!contact || contact?.uuid === id) {
        history.push("/contacts");
      } else {
        let sectionPath = "info";
        if (section) {
          sectionPath = subsection ? `${section}/${subsection}` : section;
        }
        history.push(`/contacts/${contact?.uuid}/${sectionPath}`);
      }
    },
    [detailsPageMatch?.params, history, isMobile, toggleSideMenu]
  );

  const toggleEditContactForm = useCallback(
    (contact: Contact | undefined) => {
      if (openContactFormSidebar) {
        setCurrentlyEditedContact(undefined);
        setOpenContactFormSidebar(false);
      } else {
        setCurrentlyEditedContact(contact);
        setOpenContactFormSidebar(true);
      }
    },
    [openContactFormSidebar]
  );

  const renderContact = useCallback(
    (index: number) => {
      const contact = filteredContacts[index];

      return (
        <div
          key={contact.uuid}
          className={classNames(
            "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:ring-2 ring-inset ring-hotspotty-400 border-b border-gray-200 dark:border-gray-500  dark:ring-white",
            detailsPageMatch?.params?.id === contact.uuid && "ring-2"
          )}
          onClick={() => toggleContact(contact)}
        >
          <ContactComponent contactId={contact.uuid} />
          <ContactActionsButton
            contact={contact}
            toggleEditContactForm={toggleEditContactForm}
          />
        </div>
      );
    },
    [
      detailsPageMatch?.params?.id,
      filteredContacts,
      toggleContact,
      toggleEditContactForm
    ]
  );

  const isLoading = useMemo(
    () => isEmpty(contacts) && !getIsReady([LoadingKey.workspaceContacts]),
    [contacts, getIsReady]
  );

  const renderFilterSection = useMemo(
    () => (
      <div>
        <div className="flex-1 flex px-4 pb-2">
          <FilterLabels
            className="mt-2 w-full"
            entity={Entity.contact}
            filteredLabels={filteredContactLabels}
            smartLabels={keys(smartLabelFilters[Entity.contact]).map((key) =>
              getSmartLabelFromKey(key, Entity.contact)
            )}
            filterKey={LabelFilterKey.mapContacts}
          />
        </div>
      </div>
    ),
    [filteredContactLabels, smartLabelFilters]
  );

  const renderContent = useMemo(() => {
    if (currentWorkspaceIsLocked) {
      return (
        <LockedWorkspace
          title="Payment failed"
          paragraphs={[
            "Your subscription payment failed. Please resolve the issue with your payment method."
          ]}
          buttonText="Update payment details"
          buttonLink="/workspaces/settings/billing"
        />
      );
    }

    if (!currentWorkspaceAllowedForCommunityPlan) {
      return <LockedWorkspaceByCommunityPlan />;
    }

    if (membersLimitExceeded) {
      return <LockedWorkspaceByMembers />;
    }

    if (isLoading) {
      return <CenteredLoading className="h-full" />;
    }

    if (isEmpty(contacts)) {
      return (
        <FeatureExplanation
          paragraphs={contactsExplanationParagraphs.concat(
            "Locations and currently installed hotspots linked to the listed contacts are available on the map." as any
          )}
        >
          <button
            type="button"
            className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => setOpenContactFormSidebar(true))}
          >
            Add contact
          </button>
        </FeatureExplanation>
      );
    }

    if (isEmpty(filteredContacts)) {
      return (
        <>
          {renderFilterSection}

          <div className="z-10 sticky top-0 border-b border-t border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
            <div className="text-sm font-medium">0 contacts</div>
          </div>
          <div className="px-6 pt-6 pb-4">
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              No contacts to show.
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        {renderFilterSection}

        <div className="z-10 sticky top-0 border-t border-b border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
          <div className="text-sm font-medium">
            {filteredContacts.length.toLocaleString(undefined)} contact
            {filteredContacts.length !== 1 && "s"}
          </div>
        </div>

        <VirtualScroll
          className="overflow-y-auto"
          minItemHeight={80}
          totalLength={filteredContacts.length}
          renderItem={renderContact}
        />
      </>
    );
  }, [
    contacts,
    currentWorkspaceAllowedForCommunityPlan,
    currentWorkspaceIsLocked,
    filteredContacts,
    isLoading,
    membersLimitExceeded,
    renderContact,
    renderFilterSection,
    wrapActionWithAuth
  ]);

  return (
    <>
      <ContactFormSidebar
        contact={currentlyEditedContact}
        open={openContactFormSidebar}
        toggleEditContactForm={toggleEditContactForm}
      />

      {renderContent}
    </>
  );
};

export default ContactsList;
